<template>
  <div class="relative flex flex-col w-full">
    <!-- Badge flotante — no afecta el layout del DOM (absolute) -->
    <transition name="fade">
      <div v-if="isSaving"
        class="absolute top-0 right-0 flex items-center gap-2 px-3 py-1 text-xs font-medium text-blue-dark bg-blue-lightest border border-blue-lighter rounded-full shadow z-10">
        <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-small" type="is-info"></b-icon>
        <span>{{ $t('saving') }}</span>
      </div>
    </transition>

    <!-- Layout responsivo: una columna por defecto, dos columnas en md -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <!-- Columna izquierda -->
      <div class="flex flex-col space-y-4">

        <!-- Solo para Progress: Algoritmo de avance y ponderación -->
        <expandable-panel v-if="isProgressConfig" :item="_GetOptionsConfig('config_advance_algorithm')">
          <div class="flex flex-col space-y-2 sm:space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <span class="text-sm text-gray-700 sm:ml-4 sm:text-right flex-shrink-0">
                <span class="font-medium text-gray-800">Metodo de registro de avance</span>
              </span>
              <b-select size="is-small" placeholder="Seleccionar"
                v-model="form.advance_method" class="w-full sm:w-auto sm:min-w-48" expanded>
                <option v-for="(item, i) in advanceMethodOptions"
                  :value="item.value" :key="'amo_'+i">{{ item.text }}</option>
              </b-select>
            </div>
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <span class="text-sm text-gray-700 sm:ml-4 sm:text-right flex-shrink-0">
                <span class="font-medium text-gray-800">Criterio de ponderación:</span>
              </span>
              <b-select size="is-small"
                placeholder="Seleccionar" v-model="form.weighting_criteria"
                class="w-full sm:w-auto sm:min-w-48"
                expanded
                >
                <option v-for="(item, i) in stateWeightingCriteriaOptions"
                  :value="item.value" :key="'swco_'+i">{{ item.text }}</option>
              </b-select>
            </div>
          </div>
        </expandable-panel>

        <expandable-panel :item="_GetOptionsConfig('config_visibility_statuses')"
          mode="option-only"
          optionType="select"
          :select-options="statusesVisibilityOptions"
          :default-selected-value="form.visibility_policy"
          @update:selectedValue="form.visibility_policy = $event"
        >
        </expandable-panel>

        <expandable-panel :item="_GetOptionsConfig('config_actions_permitted')"
          mode="mixed"
          optionType="switch"
          :default-active="form.actions_permitted"
          @input="form.actions_permitted = $event"
        >
        <div class="flex flex-col space-y-2 sm:space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <span class="text-sm text-gray-700 sm:ml-4 sm:text-right flex-shrink-0">
                <span class="font-medium text-gray-800">Permitir volver a un estado anterior</span>
              </span>
              <b-switch
                size="is-small"
                v-model="form.allow_rollback_to_previous"
                :disabled="!form.actions_permitted"
              />
            </div>
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <span class="text-sm text-gray-700 sm:ml-4 sm:text-right flex-shrink-0">
                <span class="font-medium text-gray-800">Permitir retroceder hasta el estado inicial</span>
              </span>
              <b-switch
                size="is-small"
                v-model="form.allow_reset_to_initial"
                :disabled="!form.actions_permitted"
              />
            </div>
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <span class="text-sm text-gray-700 sm:ml-4 sm:text-right flex-shrink-0">
                <span class="font-medium text-gray-800">Mantener los estados como archivados </span>
              </span>
              <b-switch
                size="is-small"
                v-model="form.allow_archiving_states"
                :disabled="!form.actions_permitted"
              />
            </div>
          </div>
        </expandable-panel>

        <expandable-panel :item="_GetOptionsConfig('config_bulk_actions')"
          mode="option-only"
          optionType="button"
          :button-options="{
            label: $t('open'),
            icon: 'external-link-alt',
            type: 'is-primary',
          }"
          @button-click="_OnClickReplicate"
        >
        </expandable-panel>

      </div>

      <!-- Columna derecha -->
      <div class="flex flex-col space-y-4">
        <expandable-panel :item="_GetOptionsConfig('config_statuses')">
          <layer-statuses-manager v-if="layerData"
            :initial-layer-data="layerData"
            :api="layerApi"
            :type-status="typeStatusForManager"
            :config="configForManager"
          />
        </expandable-panel>

        <expandable-panel :item="_GetOptionsConfig('config_simultaneous_statuses')"
          mode="option-only"
          optionType="switch"
          :default-active="form.allow_multi_status_entry"
          @input="form.allow_multi_status_entry = $event"
        >
        </expandable-panel>

      </div>
    </div>

    <modal-wrapper ref="modalWrapper" />
  </div>
</template>

<script>
import Form from 'vform';

import ExpandablePanel from './expandable-panel.vue';
import LayerStatusesManager from './layer-statuses-manager.vue';
import { getConfigStatusOptions } from '../../data/config-status-options';
import { advanceMethodOptions
  , stateWeightingCriteriaOptions
  , statusesVisibilityOptions
} from '../../data/select.options.js';

import { initializeFormFromConfig } from '../../helpers/layer-statuses.helper.js';
import { editPartialLayer, readLayer } from '../../services/layers.service.js';
import { addLayerStatus, editLayerStatus, linkLayerStatus, deleteLayerStatus } from '../../services/layer-statuses.service.js';

import LayerBulkActions from '../layers/LayerBulkActions.vue';
import ModalWrapper from '../../shared/components/modals/ModalWrapper.vue';

export default {
  name: 'LayerStatusesConfig',

  components: {
    ExpandablePanel,
    LayerStatusesManager,
    // LayerBulkActions,
    ModalWrapper,
  },

  props: {
    layer: {
      type: Object,
      required: true  // o default: null
    },
    configType: {
      type: String,
      default: 'progress', // 'progress' o 'incident'
      validator: (value) => ['progress', 'incident'].includes(value)
    }
  },

  data() {
    return {
      advanceMethodOptions, // Opciones de método de avance
      stateWeightingCriteriaOptions, // Opciones de criterio de ponderación
      statusesVisibilityOptions, // Opciones de visibilidad de estados
      item: null,
      optionsConfig: [], // Inicializar como array vacío para evitar errores en el template
      isInitializing: true,
      saveConfigurationTimeout: null, // Timeout para debounce del guardado
      originalFormData: null, // Valores originales del formulario para comparar cambios

      layerData: null,        // Datos crudos del backend
      isSaving: false,  // Indicador para el badge "Guardando..."

      form: new Form({
        advance_method: null,
        weighting_criteria: null,
        visibility_policy: 'public',
        allow_multi_status_entry: false,
        actions_permitted: false,
        allow_rollback_to_previous: false,
        allow_reset_to_initial: false,
        allow_archiving_states: false,
      }),
    };
  },

  async created() {
    // Cargar datos del layer primero (async)
    await this._loadLayerData();
  },

  beforeDestroy() {
    // Limpiar el timeout al destruir el componente para evitar memory leaks
    if (this.saveConfigurationTimeout) {
      clearTimeout(this.saveConfigurationTimeout);
      this.saveConfigurationTimeout = null;
    }
  },

  computed: {
    formData() {
      // Computed property que devuelve los datos del formulario
      // Esto permite observar todos los cambios de una vez
      return this.form.data();
    },

    isProgressConfig() {
      return this.configType === 'progress';
    },

    isIncidentConfig() {
      return this.configType === 'incident';
    },

    typeStatusForManager() {
      // Mapea configType a lo que espera layer-statuses-manager
      return this.configType === 'progress' ? 'status' : 'incident';
    },

    /**
     * Contrato HTTP que se inyecta al manager. Cada método retorna una Promise
     * con echo del backend (status completo con permissions) — habilita
     * optimistic UI + merge local sin GET adicional.
     *
     * El polimorfismo del endpoint backend (un POST para status/group/incident)
     * queda oculto detrás de métodos semánticamente claros.
     */
     layerApi() {
      const layerId = this.layer?.id;
      return {
        // Statuses (type='status')
        addStatus:    (payload)     => addLayerStatus(layerId, { ...payload, type: 'status' }),
        updateStatus: (id, payload) => editLayerStatus(layerId, id, payload),
        deleteStatus: (id)          => deleteLayerStatus(layerId, id),

        // Grupos (type='group')
        addGroup:     (payload)     => addLayerStatus(layerId, { ...payload, type: 'group' }),
        updateGroup:  (id, payload) => editLayerStatus(layerId, id, payload),
        deleteGroup:  (id)          => deleteLayerStatus(layerId, id),

        // Vínculo
        linkStatusToGroup: (statusId, groupId) =>
          linkLayerStatus(layerId, statusId, { group_id: groupId }),

        // Incidents (type='incident')
        addIncident:    (payload)     => addLayerStatus(layerId, { ...payload, type: 'incident' }),
        updateIncident: (id, payload) => editLayerStatus(layerId, id, payload),
        deleteIncident: (id)          => deleteLayerStatus(layerId, id),
      };
    },

    /**
     * Config para el manager, derivada de originalFormData (= valores PERSISTIDOS).
     * Solo se actualiza tras un PATCH éxito → el manager nunca refleja valores
     * que el backend rechazó.
     * Mapea form (advance_method, inglés) → storage (avance_method, español).
     */
     configForManager() {
      const persisted = this.originalFormData;
      if (!persisted) return null;
      // La visibilidad se aplica SOLO al tipo que gestiona esta instancia (progress o
      // incident). Antes se escribía el mismo policy en ambas claves, pisando la
      // visibilidad del otro tipo al guardar.
      const visibility = this.configType === 'incident'
        ? { incidents: persisted.visibility_policy }
        : { progress: persisted.visibility_policy };

      return {
        avance_method:      persisted.advance_method,
        weighting_criteria: persisted.weighting_criteria,
        visibility,
      };
    },
  },

  watch: {
    formData: {
      handler() {
        if (!this.isInitializing) {
          this._debouncedSaveConfiguration();
        }
      },
      deep: true
    }
  },

  methods: {
    _OnClickReplicate() {
      const isIncident = this.configType === 'incident';
      this.$refs.modalWrapper.show({
        component: LayerBulkActions,
        title: this.$t(isIncident ? 'replicate_incident_statuses_title' : 'replicate_progress_statuses_title'),
        props: {
          layer: this.layerData,
          context:       isIncident ? 'incident' : 'progress',
          presetOptions: isIncident
            ? { apply_form_config: true, apply_incidents: true }
            : { apply_form_config: true, apply_groups: true, apply_statuses: true },
        },
      });
    },

    async _loadLayerData() {
      try {
        // Cargar el layer con statuses e incidentStatuses
        // Usar 1 en lugar de true para que Laravel lo valide correctamente como booleano
        const response = await readLayer(this.layer.id, {
          with_group_statuses: 1,
          with_statuses: 1,
          with_incident_statuses: 1,
          with_statuses_permissions: 1,
        });

        if( response.success ) {
          this.layerData = response.layer;

          // Inicializar optionsConfig con el código del layer
          this.optionsConfig = getConfigStatusOptions(this.layerData.code);

          // Inicializar el formulario
          this.isInitializing = true;
          this._initializeForm();

          // Marcar que la inicialización ha terminado y guardar valores originales
          this.$nextTick(() => {
            this.isInitializing = false;
            this.originalFormData = { ...this.form.data() };
          });

        } else {
          this.$toast.open({
            message: response.error,
            type: "is-danger",
            position: "is-top-right",
          });
        }
      } catch (error) {
        console.error("Error cargar datos de la capa:", error);
        this.$toast.open({
          message: error.error_msg || this.$t("assignment_update_error"),
          type: "is-danger",
          position: "is-top-right",
        });
      }
    },

    _GetOptionsConfig(code) {
      if (!this.optionsConfig || !Array.isArray(this.optionsConfig)) {
        return null;
      }
      return this.optionsConfig.find(option => option.code === code) || null;
    },

    /**
     * Inicializa el formulario con los valores de layer.config
     * Usa el helper para mapeo y transformación de datos
     */
    _initializeForm() {
      if (!this.layerData) {
        return;
      }

      initializeFormFromConfig(
        this.layerData,
        this.form,
        this._initializeSpecialFields.bind(this)
      );
    },

    /**
     * Inicializa campos que requieren lógica especial o transformaciones complejas
     * @param {Object} config - Configuración del layer
     */
    _initializeSpecialFields(config) {
      // Aquí puedes agregar campos que requieren lógica especial
      // Por ejemplo, fechas, transformaciones complejas, etc.
    },

    _handleGoBack() {
      this.$emit('go-back');
    },

    _handleItemClick(item) {
      this.$emit('item-click', item);
    },

    /**
     * Debounce para el guardado de configuración
     * Espera 800ms después del último cambio antes de ejecutar el guardado
     * Esto permite que el usuario configure múltiples opciones sin disparar múltiples guardados
     */
    _debouncedSaveConfiguration() {
      // Limpiar el timeout anterior si existe
      if (this.saveConfigurationTimeout) {
        clearTimeout(this.saveConfigurationTimeout);
      }

      // Establecer nuevo timeout (800ms es un tiempo estándar para formularios de configuración)
      this.saveConfigurationTimeout = setTimeout(() => {
        this._saveConfiguration();
        this.saveConfigurationTimeout = null;
      }, 800);
    },

    /**
     * Compara los valores actuales del formulario con los originales
     * y retorna solo los campos que han cambiado
     * @param {Object} currentData - Datos actuales del formulario
     * @param {Object} originalData - Datos originales del formulario
     * @returns {Object} Objeto con solo los campos que cambiaron
     */
    _getChangedFields(currentData, originalData) {
      if (!originalData) return currentData;

      const changedFields = {};

      Object.keys(currentData).forEach(key => {
        const currentValue = currentData[key];
        const originalValue = originalData[key];

        // Comparar valores (considerando null, undefined, y tipos)
        if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
          changedFields[key] = currentValue;
        }
      });

      return changedFields;
    },

    /**
     * Guarda la configuración del formulario
     * Este método se ejecuta después del debounce
     * Solo envía los campos que realmente han cambiado
     */
    async _saveConfiguration() {
      // Obtener los datos actuales del formulario
      const formData = this.form.data();

      // Obtener solo los campos que han cambiado
      const changedFields = this._getChangedFields(formData, this.originalFormData);

      // Si no hay cambios, no enviar nada
      if (Object.keys(changedFields).length === 0) {
        return;
      }

      // Construir payload solo con los campos que cambiaron
      const payload = {
        config: {
          statuses: {
            type: this.configType, // 'progress' o 'incident'
            ...changedFields,
          },
        },
      };

      this.isSaving = true;

      try {
        const response = await editPartialLayer(this.layer.id, payload);

        if( response.success ) {
          // Actualizar los valores originales después de guardar exitosamente
          // Esto dispara el recálculo de configForManager → el manager se actualiza.
          this.originalFormData = { ...this.form.data() };

          this.$toast.open({
            message: this.$t("assignment_update_success"),
            type: "is-success",
            position: "is-top-right",
          });

          // La config de estados/ponderación puede recalcular el progreso → refresh
          // dirigido del summary (sin skeleton ni reset de navegación).
          this.$emit('epc-pictograms-reload', {
            scope: 'layer',
            layerId: this.layer.id,
            affectsProgress: true,
          })

        } else {
          this.$toast.open({
            message: response.error,
            type: "is-danger",
            position: "is-top-right",
          });
        }
      } catch (error) {
        console.error("Error al guardar datos de las capas del pictograma:", error);
        this.$toast.open({
          message: error.error_msg || this.$t("assignment_update_error"),
          type: "is-danger",
          position: "is-top-right",
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
}
</script>
